<template>
  <section class="content">
    <div class="container">
      <nav aria-label="pagination" role="navigation">
        <ul class="paginate">
          <li
            :class="[firstPageSelected() ? 'disabled' : '']"
            class="paginate-item paginate-item--prev"
          >
            <button
              type="button"
              :disabled="firstPageSelected()"
              :tabindex="firstPageSelected() ? -1 : 0"
              aria-label="Go back one page of results"
              class="button button--outline"
              @click.prevent="prevPage()"
              @keyup.enter="prevPage()"
            >
              <Icon name="fa6-solid:chevron-left" class="chevron-icon" />
              <span class="nav-text"> Back </span>
            </button>
          </li>
          <li
            v-for="(page, _, index) in pages"
            :key="index"
            :class="[
              page.selected ? 'active' : '',
              page.disabled ? 'disabled' : '',
              page.breakView ? 'paginate-item--break' : '',
            ]"
            class="paginate-item"
          >
            <div
              v-if="page.breakView"
              class="button button--reverse"
              tabindex="0"
            >
              <slot name="breakViewContent">
                <span> &hellip; </span>
              </slot>
            </div>
            <button
              v-else
              type="button"
              :disabled="page.disabled"
              :aria-label="'Go to page ' + page.content + ' of results'"
              class="button button--outline"
              tabindex="0"
              @click.prevent="handlePageSelected(page.index + 1)"
              @keyup.enter="handlePageSelected(page.index + 1)"
            >
              <span>
                {{ page.content }}
              </span>
            </button>
          </li>
          <li
            :class="[lastPageSelected() ? 'disabled' : '']"
            class="paginate-item paginate-item--next"
          >
            <button
              type="button"
              :disabled="lastPageSelected()"
              :tabindex="lastPageSelected() ? -1 : 0"
              aria-label="Go forward one page of results"
              class="button button--outline"
              @click.prevent="nextPage()"
              @keyup.enter="nextPage()"
            >
              <span class="nav-text"> Next </span>
              <Icon name="fa6-solid:chevron-right" class="chevron-icon" />
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>

<script setup lang="ts">
const emit = defineEmits(['onSetPage'])
const props = withDefaults(
  defineProps<{
    pagesTotal?: number
    value?: number
    pageRange?: number
    marginPages?: number
    routeName?: string
  }>(),
  {
    pagesTotal: 1,
    value: 1,
    pageRange: 3,
    marginPages: 1,
    routeName: undefined,
  },
)

const innerValue = ref<number>(1)

const selected = computed<number>({
  get: () => props.value || innerValue.value,
  set: (newValue) => {
    innerValue.value = newValue
  },
})

const pages = computed(() => {
  const items: Record<
    number,
    {
      index?: number
      content?: number
      selected?: boolean
      disabled?: boolean
      breakView?: boolean
    }
  > = {}
  if (props.pagesTotal <= props.pageRange) {
    for (let index = 0; index < props.pagesTotal; index++) {
      items[index] = {
        index,
        content: index + 1,
        selected: index === selected.value - 1,
      }
    }
  } else {
    const halfPageRange = Math.floor(props.pageRange / 2)

    const setPageItem = (index) => {
      items[index] = {
        index,
        content: index + 1,
        selected: index === selected.value - 1,
      }
    }

    const setBreakView = (index) => {
      items[index] = {
        disabled: true,
        breakView: true,
      }
    }

    // 1st - loop thru low end of margin pages
    for (let i = 0; i < props.marginPages; i++) {
      setPageItem(i)
    }

    // 2nd - loop thru selected range
    let selectedRangeLow = 0
    if (selected.value - halfPageRange > 0) {
      selectedRangeLow = selected.value - 1 - halfPageRange
    }

    let selectedRangeHigh = selectedRangeLow + props.pageRange - 1
    if (selectedRangeHigh >= props.pagesTotal) {
      selectedRangeHigh = props.pagesTotal - 1
      selectedRangeLow = selectedRangeHigh - props.pageRange + 1
    }

    for (
      let i = selectedRangeLow;
      i <= selectedRangeHigh && i <= props.pagesTotal - 1;
      i++
    ) {
      setPageItem(i)
    }

    // Check if there is breakView in the left of selected range
    if (selectedRangeLow > props.marginPages) {
      setBreakView(selectedRangeLow - 1)
    }

    // Check if there is breakView in the right of selected range
    if (selectedRangeHigh + 1 < props.pagesTotal - props.marginPages) {
      setBreakView(selectedRangeHigh + 1)
    }

    for (
      let i = props.pagesTotal - 1;
      i >= props.pagesTotal - props.marginPages;
      i--
    ) {
      setPageItem(i)
    }
  }
  return items
})

const handlePageSelected = (selectedPage: number) => {
  if (selected.value === selectedPage) return
  innerValue.value = selectedPage
  emit('onSetPage', selectedPage)
}

function prevPage() {
  if (selected.value <= 1) return
  handlePageSelected(selected.value - 1)
}

function nextPage() {
  if (selected.value >= props.pagesTotal) return
  handlePageSelected(selected.value + 1)
}

function firstPageSelected() {
  return selected.value === 1
}

function lastPageSelected() {
  return selected.value === props.pagesTotal || props.pagesTotal === 0
}
</script>

<style lang="less" scoped>
.paginate {
  .flex();
  .list-reset();
  .justify-center();

  .paginate-item {
    .text-center();
    margin: 0 @2px 0 0;

    @media @desktop {
      margin: @4px;
    }

    .button {
      .block();
      .text-center();
      .font-medium();
      cursor: pointer;
      padding: @gutter-xs @gutter-xs;
      min-width: @40px;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
      user-select: none;

      @media @desktop {
        width: auto;
        padding: @gutter;
      }
    }

    &.disabled {
      .button {
        cursor: not-allowed;
        background-color: transparent;
        border-color: @color-border;
        color: @color-fill-muted;

        .chevron-icon {
          color: @color-fill-muted;
        }
      }
    }

    &.paginate-item--prev,
    &.paginate-item--next {
      .button {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 36px;

        .nav-text {
          display: none;

          @media @desktop {
            display: inline-block;
            padding: 0 @gutter-xs;
          }
        }

        .chevron-icon {
          display: inline-block;
          width: 12px;
          height: 12px;
        }

        @media @desktop {
          min-height: auto;
        }
      }
    }

    &.paginate-item--break {
      margin-left: 0;
      margin-right: 0;

      .button {
        width: @20px;
        border: none;
        cursor: default;
        padding-left: 0;
        padding-right: 0;
        min-width: @20px;

        @media @desktop {
          width: auto;
        }
      }
    }

    &.active {
      .button {
        background-color: @color-primary;
        border-color: @color-primary;
        color: @color-text-reverse;
      }
    }
  }
}
</style>
